import { useState, useCallback, useRef, useEffect } from 'react';
import { filesApi } from '../services/api';
import type { UploadTask, UploadPart } from '../types';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_CONCURRENT_UPLOADS = 4; // 同时上传的文件数（增加到4）
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const UPLOAD_SPEED_WINDOW = 5000; // 计算上传速度的时间窗口（5秒）

// 上传进度跟踪
interface UploadProgress {
  loaded: number;
  total: number;
  speed: number; // bytes/s
  remainingTime: number; // seconds
}

export function useUpload(onAllUploadsComplete?: (allSucceeded: boolean) => void) {
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [uploadSpeeds, setUploadSpeeds] = useState<Record<string, UploadProgress>>({});

  const tasksRef = useRef<UploadTask[]>([]);
  const activeCountRef = useRef<number>(0);
  const callbackRef = useRef(onAllUploadsComplete);
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const progressHistoryRef = useRef<Record<string, { timestamp: number; loaded: number }[]>>({});
  
  // 保存失败的 task IDs
  const failedTaskIdsRef = useRef<Set<string>>(new Set());

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onAllUploadsComplete;
  }, [onAllUploadsComplete]);

  useEffect(() => {
    tasksRef.current = uploadTasks;
  }, [uploadTasks]);

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // 计算上传速度
  const calculateUploadSpeed = (taskId: string, loaded: number, total: number): UploadProgress => {
    const now = Date.now();
    const history = progressHistoryRef.current[taskId] || [];
    
    // 添加当前进度
    history.push({ timestamp: now, loaded });
    
    // 只保留时间窗口内的记录
    const cutoff = now - UPLOAD_SPEED_WINDOW;
    const recentHistory = history.filter(h => h.timestamp > cutoff);
    progressHistoryRef.current[taskId] = recentHistory;
    
    // 计算速度
    let speed = 0;
    if (recentHistory.length >= 2) {
      const first = recentHistory[0];
      const last = recentHistory[recentHistory.length - 1];
      const timeDiff = (last.timestamp - first.timestamp) / 1000; // seconds
      const bytesDiff = last.loaded - first.loaded;
      speed = timeDiff > 0 ? bytesDiff / timeDiff : 0;
    }
    
    // 计算剩余时间
    const remaining = loaded < total && speed > 0 
      ? Math.round((total - loaded) / speed) 
      : 0;
    
    return { loaded, total, speed, remainingTime: remaining };
  };

  // Upload a single part with retry and abort support
  const uploadPartWithRetry = async (
    key: string,
    uploadId: string,
    partNumber: number,
    chunk: Blob,
    taskId: string,
    retryCount = 0
  ): Promise<string> => {
    try {
      const arrayBuffer = await chunk.arrayBuffer();
      
      // 获取当前任务的 AbortController
      const abortController = abortControllersRef.current[taskId];
      
      const response = await filesApi.uploadPart(
        key, 
        uploadId, 
        partNumber, 
        arrayBuffer,
        abortController?.signal
      );

      if (!response || typeof response !== 'object') {
        throw new Error('分片上传失败');
      }

      const partData = (response as any).data;
      if (!partData?.etag) {
        throw new Error('分片上传失败：返回数据格式错误');
      }

      return partData.etag;
    } catch (error) {
      // 检查是否是被取消的
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      
      if (retryCount < MAX_RETRIES) {
        await delay(RETRY_DELAY * (retryCount + 1));
        return uploadPartWithRetry(key, uploadId, partNumber, chunk, taskId, retryCount + 1);
      }
      throw error;
    }
  };

  // Upload single file with resumable support
  const uploadSingleFile = async (taskId: string, resumeFromPart: number = 0) => {
    const task = tasksRef.current.find((t) => t.id === taskId);
    if (!task || (task.status !== 'pending' && task.status !== 'uploading')) {
      return;
    }

    // 创建 AbortController
    if (!abortControllersRef.current[taskId]) {
      abortControllersRef.current[taskId] = new AbortController();
    }

    // Mark as uploading
    setUploadTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: 'uploading' } : t))
    );

    try {
      // 1. Initialize upload (or use existing if resuming)
      let uploadId: string;
      let key: string;
      let totalParts: number;
      let startPart = resumeFromPart;

      if (task.uploadId && task.key && task.parts) {
        // 断点续传：使用已存在的 upload session
        uploadId = task.uploadId;
        key = task.key;
        totalParts = task.parts.length;
      } else {
        // 新上传
        const initResponse = await filesApi.initUpload({
          fileName: task.fileName,
          fileSize: task.fileSize,
          fileType: task.fileType,
          chunkSize: CHUNK_SIZE,
        });

        if ((initResponse as any)?.code === 'FILE_ALREADY_EXISTS') {
          setUploadTasks((prev) =>
            prev.map((t) =>
              t.id === taskId ? { ...t, status: 'error', error: `文件已存在` } : t
            )
          );
          // 记录失败的 task
          failedTaskIdsRef.current.add(taskId);
          return;
        }

        const data = (initResponse as any).data;
        uploadId = data.uploadId;
        key = data.key;
        totalParts = data.parts;

        // Initialize parts
        const parts: UploadPart[] = Array(totalParts)
          .fill(null)
          .map((_, i) => ({
            partNumber: i + 1,
            status: 'pending' as const,
            size: Math.min(CHUNK_SIZE, task.fileSize - i * CHUNK_SIZE),
          }));

        setUploadTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, uploadId, key, parts } : t))
        );
      }

      // 2. Upload parts (支持断点续传)
      const results: { partNumber: number; etag: string }[] = [];
      let uploadedBytes = startPart * CHUNK_SIZE;

      for (let i = startPart; i < totalParts; i++) {
        // 检查是否被取消
        if (abortControllersRef.current[taskId]?.signal.aborted) {
          throw new Error('Upload cancelled');
        }

        const partNumber = i + 1;
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, task.fileSize);
        const chunk = task.file.slice(start, end);

        // Update to uploading
        setUploadTasks((prev) =>
          prev.map((t) =>
            t.id === taskId && t.parts
              ? {
                  ...t,
                  parts: t.parts.map((p) =>
                    p.partNumber === partNumber ? { ...p, status: 'uploading' as const } : p
                  ),
                }
              : t
          )
        );

        const etag = await uploadPartWithRetry(key, uploadId, partNumber, chunk, taskId);
        results.push({ partNumber, etag });

        // Update progress and speed
        uploadedBytes += chunk.size;
        const progress = calculateUploadSpeed(taskId, uploadedBytes, task.fileSize);
        
        setUploadSpeeds((prev) => ({
          ...prev,
          [taskId]: progress,
        }));

        setUploadTasks((prev) =>
          prev.map((t) => {
            if (t.id !== taskId || !t.parts) return t;
            const newParts = t.parts.map((p) =>
              p.partNumber === partNumber ? { ...p, status: 'completed' as const, etag } : p
            );
            const completed = newParts.filter((p) => p.status === 'completed').length;
            return {
              ...t,
              parts: newParts,
              completedParts: completed,
              progress: Math.round((completed / totalParts) * 100),
            };
          })
        );
      }

      // 3. Complete upload
      await filesApi.completeUpload({
        uploadId,
        key,
        fileName: task.fileName,
        fileType: task.fileType,
        fileSize: task.fileSize,
        parts: results.sort((a, b) => a.partNumber - b.partNumber),
      });

      // Mark completed
      setUploadTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'completed', progress: 100 } : t))
      );

      // Cleanup
      delete abortControllersRef.current[taskId];
      delete progressHistoryRef.current[taskId];
      setUploadSpeeds((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });

      // Track completion count for UI
      setCompletedCount((prev) => prev + 1);
    } catch (error) {
      // 检查是否是被取消的
      if (error instanceof Error && error.message === 'Upload cancelled') {
        setUploadTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, status: 'paused' } : t))
        );
        // 记录被取消的 task，不视为成功
        failedTaskIdsRef.current.add(taskId);
        return;
      }
      
      // 优先使用后端返回的错误信息
      let errorMessage = '上传失败';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setUploadTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'error', error: errorMessage } : t))
      );
      
      // 记录失败的 task
      failedTaskIdsRef.current.add(taskId);
    }
  };

  // Process uploads with proper concurrency control
  const processUploads = async (taskIds: string[], resumeFromPart: number = 0) => {
    const completedTasks = new Set<string>();
    const totalTasks = taskIds.length;

    const processNext = async (): Promise<void> => {
      // Find next pending task
      const pendingTaskId = taskIds.find((id) => {
        const task = tasksRef.current.find((t) => t.id === id);
        return task?.status === 'pending' && !completedTasks.has(id);
      });

      if (!pendingTaskId) {
        return;
      }
      activeCountRef.current++;

      try {
        await uploadSingleFile(pendingTaskId, resumeFromPart);
      } catch (error) {
        // uploadSingleFile 已经处理了错误和状态更新，这里只需要确保任务被标记为完成
        // 不要重新抛出错误，让其他任务继续执行
      } finally {
        // 无论成功失败，都添加到 completedTasks
        completedTasks.add(pendingTaskId);
        
        activeCountRef.current--;
        // If there are more tasks, process them
        const hasMorePending = taskIds.some((id) => {
          const task = tasksRef.current.find((t) => t.id === id);
          return task?.status === 'pending' && !completedTasks.has(id);
        });

        if (hasMorePending) {
          await processNext();
        }
      }
    };

    // Start concurrent uploads
    const workers: Promise<void>[] = [];
    for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, totalTasks); i++) {
      workers.push(processNext());
    }

    // Use Promise.allSettled to ensure all workers complete
    await Promise.allSettled(workers);

    // Check if all completed
    if (completedTasks.size === totalTasks) {
      // Check if all tasks succeeded
      const allSucceeded = failedTaskIdsRef.current.size === 0;
      
      console.log('[Upload] All tasks completed:', {
        totalTasks,
        completedTasks: completedTasks.size,
        failedTasks: failedTaskIdsRef.current.size,
        failedTaskIds: Array.from(failedTaskIdsRef.current),
        allSucceeded
      });
      
      // Use ref to always get the latest callback
      setTimeout(() => {
        callbackRef.current?.(allSucceeded);
      }, 100);
      
      // Only clear failed tasks ref if this is the last batch
      // Check if there are still tasks in progress
      const tasksInProgress = tasksRef.current.filter(t => t.status === 'pending' || t.status === 'uploading');
      if (tasksInProgress.length === 0) {
        failedTaskIdsRef.current.clear();
      }
    }
  };

  // Add new upload tasks
  const addUploadTasks = useCallback(
    (files: File[]) => {
      // Clear failed tasks ref for new batch
      failedTaskIdsRef.current.clear();
      
      const newTasks: UploadTask[] = files.map((file, index) => ({
        id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        status: 'pending' as const,
        progress: 0,
        completedParts: 0,
        parts: [],
      }));

      const taskIds = newTasks.map((t) => t.id);

      // Add to state
      setUploadTasks((prev) => {
        const updated = [...prev, ...newTasks];
        tasksRef.current = updated;
        return updated;
      });

      // Start processing
      setTimeout(() => {
        processUploads(taskIds);
      }, 0);
    },
    []
  );

  // Pause upload
  const pauseUpload = useCallback((taskId: string) => {
    setUploadTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'paused' } : t)));
  }, []);

  // Resume upload with breakpoint continuation
  const resumeUpload = useCallback(
    (taskId: string) => {
      const task = tasksRef.current.find((t) => t.id === taskId);
      if (task && (task.status === 'paused' || task.status === 'error')) {
        // 计算从哪个分片开始续传
        let resumeFromPart = 0;
        if (task.parts) {
          // 找到第一个未完成的分片
          const pendingPart = task.parts.find(p => p.status !== 'completed');
          if (pendingPart) {
            resumeFromPart = pendingPart.partNumber - 1;
          }
        }
        
        // 重新创建 AbortController
        abortControllersRef.current[taskId] = new AbortController();
        
        setUploadTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'pending' } : t)));
        processUploads([taskId], resumeFromPart);
      }
    },
    []
  );

  // Cancel upload with AbortController
  const cancelUpload = useCallback(async (taskId: string) => {
    const task = tasksRef.current.find((t) => t.id === taskId);
    
    // 触发 AbortController 取消上传
    const abortController = abortControllersRef.current[taskId];
    if (abortController) {
      abortController.abort();
    }
    
    activeCountRef.current = Math.max(0, activeCountRef.current - 1);
    
    // 调用后端 abort 接口清理资源
    if (task && task.uploadId && task.key) {
      try {
        await filesApi.abortUpload({
          key: task.key,
          uploadId: task.uploadId,
          fileSize: task.fileSize,
        });
      } catch (error) {
        console.error('Failed to abort upload on server:', error);
      }
    }
    
    // 清理资源
    delete abortControllersRef.current[taskId];
    delete progressHistoryRef.current[taskId];
    setUploadSpeeds((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    
    setUploadTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  // Clear completed
  const clearCompleted = useCallback(() => {
    setUploadTasks((prev) => prev.filter((t) => t.status !== 'completed'));
    setCompletedCount(0);
  }, []);

  // Format upload speed for display
  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond === 0) return '-';
    if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(0)} B/s`;
    if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
  };

  // Format remaining time for display
  const formatRemainingTime = (seconds: number): string => {
    if (seconds === 0) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  return {
    uploadTasks,
    completedCount,
    isUploading: activeCountRef.current > 0,
    uploadSpeeds,
    formatSpeed,
    formatRemainingTime,
    addUploadTasks,
    pauseUpload,
    resumeUpload,
    cancelUpload,
    clearCompleted,
  };
}
